import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "utils/api";
import EmployeeCalendarTab from "./EmployeeCalendarTab";
import "styles/employee.css";

const WEEKDAYS = [
  { value: 1, label: "Montag" },
  { value: 2, label: "Dienstag" },
  { value: 3, label: "Mittwoch" },
  { value: 4, label: "Donnerstag" },
  { value: 5, label: "Freitag" },
  { value: 6, label: "Samstag" },
  { value: 0, label: "Sonntag" },
];

const KIND_LABELS = {
  recurring: "Wöchentlich",
  "one-time": "Einmalig",
  exception: "Einmalig",
};

const ENTRY_MODE_LABELS = {
  blocked: "Gesperrt",
  available: "Verfügbar",
  closed: "Ganzer Tag",
};

const entryModeFromFields = (entry) => {
  if (entry?.entryMode) return entry.entryMode;
  if (entry?.unavailable && !entry?.startTime) return "closed";
  if (entry?.unavailable) return "blocked";
  return "available";
};

const emptyForm = (isAdmin = false) => ({
  kind: isAdmin ? "recurring" : "one-time",
  dayOfWeek: 1,
  date: "",
  entryMode: "blocked",
  startTime: "12:00",
  endTime: "13:00",
});

const weekdayLabel = (dayOfWeek) =>
  WEEKDAYS.find((d) => d.value === dayOfWeek)?.label || "—";

const entryListClassName = (entry) => {
  const mode = entryModeFromFields(entry);
  if (mode === "closed") return "is-unavailable";
  if (mode === "blocked") return "is-blocked";
  return "";
};

const formatEntryLabel = (entry) => {
  if (!entry) return "—";
  const mode = entryModeFromFields(entry);
  if (mode === "closed") {
    if (entry.kind === "recurring") {
      return `${weekdayLabel(entry.dayOfWeek)}: ganzer Tag nicht verfügbar`;
    }
    return `${entry.date || "—"}: ganzer Tag nicht verfügbar`;
  }
  const range = `${entry.startTime || "—"}–${entry.endTime || "—"}`;
  if (mode === "blocked") {
    if (entry.kind === "recurring") {
      return `${weekdayLabel(entry.dayOfWeek)}: gesperrt ${range}`;
    }
    return `${entry.date || "—"}: gesperrt ${range}`;
  }
  if (entry.kind === "recurring") {
    return `${weekdayLabel(entry.dayOfWeek)}: ${range}`;
  }
  return `${entry.date || "—"}: ${range}`;
};

const ACTION_LABELS = {
  create: "Erstellt",
  update: "Geändert",
  delete: "Gelöscht",
};

const normalizeSnapshotEntry = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  return {
    kind: raw.kind,
    dayOfWeek: raw.dayOfWeek,
    date: raw.date,
    entryMode: entryModeFromFields(raw),
    unavailable: Boolean(raw.unavailable),
    startTime: raw.startTime,
    endTime: raw.endTime,
  };
};

const formatHistorySnapshotLine = (entry) => {
  const normalized = normalizeSnapshotEntry(entry);
  if (!normalized?.kind) return null;
  const kindLabel = KIND_LABELS[normalized.kind] || normalized.kind;
  const modeLabel = ENTRY_MODE_LABELS[normalized.entryMode] || normalized.entryMode;
  return `${kindLabel} · ${modeLabel} · ${formatEntryLabel(normalized)}`;
};

const formatHistoryDetails = (item) => {
  const { action, snapshot } = item;

  if (action === "update" && snapshot?.before && snapshot?.after) {
    const before = formatHistorySnapshotLine(snapshot.before);
    const after = formatHistorySnapshotLine(snapshot.after);
    return { before, after, single: null };
  }

  const single = formatHistorySnapshotLine(snapshot);
  return { before: null, after: null, single };
};

const HistoryEntry = ({ item }) => {
  const details = formatHistoryDetails(item);
  const when = new Date(item.createdAt).toLocaleString("de-DE", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  return (
    <li className={`employee-history-item employee-history-item--${item.action}`}>
      <div className="employee-history-item-head">
        <span className={`employee-history-action employee-history-action--${item.action}`}>
          {ACTION_LABELS[item.action] || item.action}
        </span>
        <time className="employee-history-time" dateTime={item.createdAt}>
          {when}
        </time>
      </div>
      <div className="employee-history-item-body">
        {details.single && <p className="employee-history-detail">{details.single}</p>}
        {details.before && details.after && (
          <div className="employee-history-diff">
            <p>
              <span className="employee-history-diff-label">Vorher:</span> {details.before}
            </p>
            <p>
              <span className="employee-history-diff-label">Nachher:</span> {details.after}
            </p>
          </div>
        )}
        {!details.single && !details.before && (
          <p className="employee-history-detail employee-muted">Keine Details verfügbar.</p>
        )}
      </div>
      <p className="employee-history-meta">
        {item.changedBy?.name ? (
          <>
            <span>{item.changedBy.name}</span>
            {item.changedBy.email ? (
              <span className="employee-history-email"> · {item.changedBy.email}</span>
            ) : null}
          </>
        ) : (
          <span className="employee-muted">Unbekannter Nutzer</span>
        )}
        {item.entryId ? (
          <span className="employee-history-id"> · Eintrag {item.entryId.slice(-6)}</span>
        ) : null}
      </p>
    </li>
  );
};

const AffectedBookingsDialog = ({ bookings, onConfirm, onCancel, actionLabel }) => (
  <div className="employee-dialog-backdrop" role="presentation">
    <div className="employee-dialog" role="dialog" aria-modal="true">
      <h3>Betroffene Buchungen</h3>
      <p>
        Es gibt {bookings.length} bestätigte Buchung(en), die von dieser Änderung betroffen
        wären. Möchtest du trotzdem fortfahren?
      </p>
      <ul className="employee-booking-list">
        {bookings.map((b) => (
          <li key={b.id}>
            <strong>{b.formattedStart}</strong>
            {b.customerName ? ` — ${b.customerName}` : ""}
            <span className="employee-booking-status">{b.status}</span>
          </li>
        ))}
      </ul>
      <div className="employee-dialog-actions">
        <button type="button" className="employee-btn employee-btn--ghost" onClick={onCancel}>
          Abbrechen
        </button>
        <button type="button" className="employee-btn employee-btn--danger" onClick={onConfirm}>
          {actionLabel}
        </button>
      </div>
    </div>
  </div>
);

const AvailabilityManager = ({ isAdmin = false }) => {
  const [entries, setEntries] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [form, setForm] = useState(() => emptyForm(isAdmin));
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [affectedDialog, setAffectedDialog] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const requests = [
        api.get("/employee/availability"),
        api.get("/employee/bookings/upcoming"),
      ];
      if (isAdmin) {
        requests.push(api.get("/employee/availability/history"));
      }
      const results = await Promise.all(requests);
      setEntries(results[0].data.entries || []);
      setBookings(results[1].data.bookings || []);
      if (isAdmin && results[2]) {
        setHistory(results[2].data.history || []);
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Daten konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!success) return undefined;
    const timer = setTimeout(() => setSuccess(null), 4000);
    return () => clearTimeout(timer);
  }, [success]);

  const groupedRecurring = useMemo(() => {
    const map = new Map(WEEKDAYS.map((d) => [d.value, []]));
    entries
      .filter((e) => e.kind === "recurring")
      .forEach((e) => {
        if (map.has(e.dayOfWeek)) map.get(e.dayOfWeek).push(e);
      });
    return map;
  }, [entries]);

  const specialEntries = useMemo(
    () => entries.filter((e) => e.kind === "one-time" || e.kind === "exception"),
    [entries]
  );

  const resetForm = () => {
    setForm(emptyForm(isAdmin));
    setEditingId(null);
  };

  const startEdit = (entry) => {
    if (!isAdmin && entry.kind === "recurring") return;
    setEditingId(entry.id);
    setForm({
      kind: entry.kind === "exception" ? "one-time" : entry.kind,
      dayOfWeek: entry.dayOfWeek ?? 1,
      date: entry.date || "",
      entryMode: entryModeFromFields(entry) === "closed" ? "blocked" : entryModeFromFields(entry),
      startTime: entry.startTime || "12:00",
      endTime: entry.endTime || "13:00",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async (event, confirm = false) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        kind: isAdmin ? form.kind : "one-time",
        dayOfWeek: form.kind === "recurring" ? form.dayOfWeek : undefined,
        date: form.kind !== "recurring" ? form.date : undefined,
        entryMode: form.entryMode,
        startTime: form.startTime,
        endTime: form.endTime,
        confirm,
      };

      if (editingId) {
        await api.patch(`/employee/availability/${editingId}`, payload);
        setSuccess("Verfügbarkeit aktualisiert.");
      } else {
        await api.post("/employee/availability", payload);
        setSuccess("Verfügbarkeit gespeichert.");
      }
      resetForm();
      await loadData();
      setAffectedDialog(null);
    } catch (err) {
      if (err?.response?.status === 409 && err?.response?.data?.code === "AFFECTED_BOOKINGS") {
        setAffectedDialog({
          bookings: err.response.data.affectedBookings,
          action: editingId ? "update" : "delete",
          entryId: editingId,
        });
      } else {
        setError(err?.response?.data?.message || "Speichern fehlgeschlagen.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (entryId, confirm = false) => {
    setSubmitting(true);
    setError(null);
    try {
      await api.delete(`/employee/availability/${entryId}`, {
        data: { confirm },
      });
      setSuccess("Verfügbarkeit entfernt.");
      if (editingId === entryId) resetForm();
      await loadData();
      setAffectedDialog(null);
    } catch (err) {
      if (err?.response?.status === 409 && err?.response?.data?.code === "AFFECTED_BOOKINGS") {
        setAffectedDialog({
          bookings: err.response.data.affectedBookings,
          action: "delete",
          entryId,
        });
      } else {
        setError(err?.response?.data?.message || "Löschen fehlgeschlagen.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const confirmAffected = async () => {
    if (!affectedDialog) return;
    if (affectedDialog.action === "delete") {
      await handleDelete(affectedDialog.entryId, true);
    } else {
      const fakeEvent = { preventDefault: () => {} };
      await handleSubmit(fakeEvent, true);
    }
  };

  if (loading) {
    return <div className="employee-empty">Lade Verfügbarkeit…</div>;
  }

  return (
    <div className="employee-dashboard">
      {affectedDialog && (
        <AffectedBookingsDialog
          bookings={affectedDialog.bookings}
          onConfirm={confirmAffected}
          onCancel={() => setAffectedDialog(null)}
          actionLabel={
            affectedDialog.action === "delete" ? "Trotzdem löschen" : "Trotzdem speichern"
          }
        />
      )}

      <header className="employee-header">
        <div>
          <p className="employee-kicker">Verfügbarkeit</p>
          <h1>
            {isAdmin ? "Meine" : "Meine"} <em>Zeiten</em>
          </h1>
          <p className="employee-lead">
            {isAdmin
              ? "Verfügbarkeiten verwalten. Kunden können nur innerhalb dieser Zeiten buchen."
              : "Lege einmalige Verfügbarkeit oder Abwesenheit für bestimmte Tage fest. Änderungen gelten sofort im Buchungskalender."}
          </p>
        </div>
      </header>

      {error && <div className="auth-alert is-error">{error}</div>}
      {success && <div className="auth-alert is-success">{success}</div>}

      <div className="employee-grid">
        <section className="employee-panel">
          <h2>{editingId ? "Verfügbarkeit bearbeiten" : "Verfügbarkeit hinzufügen"}</h2>
          <form className="employee-form" onSubmit={handleSubmit}>
            {isAdmin && (
              <div className="employee-form-row">
                <label htmlFor="avail-kind">Typ</label>
                <select
                  id="avail-kind"
                  value={form.kind}
                  onChange={(e) => setForm({ ...form, kind: e.target.value })}
                >
                  <option value="recurring">Wöchentlich (regelmäßig)</option>
                  <option value="one-time">Einmalig (bestimmtes Datum)</option>
                </select>
              </div>
            )}

            {isAdmin && form.kind === "recurring" ? (
              <div className="employee-form-row">
                <label htmlFor="avail-weekday">Wochentag</label>
                <select
                  id="avail-weekday"
                  value={form.dayOfWeek}
                  onChange={(e) =>
                    setForm({ ...form, dayOfWeek: Number(e.target.value) })
                  }
                >
                  {WEEKDAYS.map((d) => (
                    <option key={d.value} value={d.value}>
                      {d.label}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div className="employee-form-row">
                <label htmlFor="avail-date">Datum</label>
                <input
                  id="avail-date"
                  type="date"
                  required
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                />
              </div>
            )}

            <div className="employee-form-row">
              <label htmlFor="avail-entry-mode">Art</label>
              <select
                id="avail-entry-mode"
                value={form.entryMode}
                onChange={(e) => setForm({ ...form, entryMode: e.target.value })}
              >
                <option value="blocked">Nicht verfügbar (Zeitraum)</option>
                <option value="available">Verfügbar (Zeitraum)</option>
              </select>
            </div>

            <div className="employee-form-row employee-form-row--times">
                <div>
                  <label htmlFor="avail-start">
                    {form.entryMode === "blocked" ? "Gesperrt von" : "Von"}
                  </label>
                  <input
                    id="avail-start"
                    type="time"
                    required
                    value={form.startTime}
                    onChange={(e) => setForm({ ...form, startTime: e.target.value })}
                  />
                </div>
                <div>
                  <label htmlFor="avail-end">
                    {form.entryMode === "blocked" ? "Gesperrt bis" : "Bis"}
                  </label>
                  <input
                    id="avail-end"
                    type="time"
                    required
                    value={form.endTime}
                    onChange={(e) => setForm({ ...form, endTime: e.target.value })}
                  />
                </div>
              </div>

            <div className="employee-form-actions">
              <button type="submit" className="employee-btn" disabled={submitting}>
                {submitting ? "Speichern…" : editingId ? "Aktualisieren" : "Speichern"}
              </button>
              {editingId && (
                <button
                  type="button"
                  className="employee-btn employee-btn--ghost"
                  onClick={resetForm}
                >
                  Abbrechen
                </button>
              )}
            </div>
          </form>
        </section>

        {isAdmin && (
          <section className="employee-panel">
            <h2>Wöchentliche Verfügbarkeit</h2>
            <div className="employee-week-grid">
              {WEEKDAYS.map((day) => {
                const dayEntries = groupedRecurring.get(day.value) || [];
                return (
                  <div key={day.value} className="employee-week-day">
                    <h3>{day.label}</h3>
                    {dayEntries.length === 0 ? (
                      <p className="employee-muted">Keine Zeiten</p>
                    ) : (
                      <ul>
                        {dayEntries.map((entry) => (
                          <li key={entry.id} className={entryListClassName(entry)}>
                            <span>{formatEntryLabel(entry)}</span>
                            <span className="employee-entry-actions">
                              <button type="button" onClick={() => startEdit(entry)}>
                                Bearbeiten
                              </button>
                              <button
                                type="button"
                                className="is-danger"
                                onClick={() => handleDelete(entry.id)}
                                disabled={submitting}
                              >
                                Entfernen
                              </button>
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        <section className="employee-panel">
          <h2>Einmalige Verfügbarkeiten</h2>
          {specialEntries.length === 0 ? (
            <p className="employee-muted">Noch keine einmaligen Verfügbarkeiten.</p>
          ) : (
            <ul className="employee-special-list">
              {specialEntries.map((entry) => (
                <li key={entry.id} className={entryListClassName(entry)}>
                  <div>
                    <span className="employee-kind-badge">{KIND_LABELS[entry.kind]}</span>
                    <span className="employee-kind-badge employee-kind-badge--mode">
                      {ENTRY_MODE_LABELS[entryModeFromFields(entry)]}
                    </span>
                    <span>{formatEntryLabel(entry)}</span>
                  </div>
                  <span className="employee-entry-actions">
                    <button type="button" onClick={() => startEdit(entry)}>
                      Bearbeiten
                    </button>
                    <button
                      type="button"
                      className="is-danger"
                      onClick={() => handleDelete(entry.id)}
                      disabled={submitting}
                    >
                      Entfernen
                    </button>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="employee-panel">
          <h2>Kommende Buchungen</h2>
          {bookings.length === 0 ? (
            <p className="employee-muted">Keine anstehenden Buchungen.</p>
          ) : (
            <ul className="employee-booking-list">
              {bookings.map((b) => (
                <li key={b.id}>
                  <strong>{b.formattedStart}</strong>
                  {b.customerName ? ` — ${b.customerName}` : ""}
                  <span className="employee-booking-status">{b.status}</span>
                </li>
              ))}
            </ul>
          )}
        </section>

        {!isAdmin && <EmployeeCalendarTab embedded />}

        {isAdmin && (
          <section className="employee-panel employee-panel--full">
            <div className="employee-history-header">
              <div>
                <h2>Verlauf</h2>
                <p className="employee-muted employee-history-lead">
                  Protokoll aller Änderungen an den Verfügbarkeiten.
                </p>
              </div>
              <button
                type="button"
                className="employee-btn employee-btn--ghost"
                onClick={() => setShowHistory((v) => !v)}
              >
                {showHistory ? "Ausblenden" : "Anzeigen"}
              </button>
            </div>
            {showHistory && (
              <ul className="employee-history-list">
                {history.length === 0 ? (
                  <li className="employee-muted">Noch keine Einträge.</li>
                ) : (
                  history.map((item) => <HistoryEntry key={item.id} item={item} />)
                )}
              </ul>
            )}
          </section>
        )}
      </div>
    </div>
  );
};

export default AvailabilityManager;
